import type { DataTableParams } from '@/shared/ui/data-table';

/**
 * The one local query engine the agreements lists share.
 *
 * WHY THIS EXISTS
 * ---------------
 * The agreements reads are whole-collection reads: `listDocuments` and
 * `listMyRequirements` take no query parameters at all, and `listExecutions`
 * takes only a status. So the narrowing a user asks for — a search term, a
 * status tab, a column filter — has to happen on the client, and it has to
 * happen the SAME way on all three pages or the three lists will disagree about
 * what "search" means.
 *
 * This mirrors the tenants list page deliberately. That page is the reference
 * for what a list in this console does, and a second, subtly different
 * implementation of the same idea is how two lists start behaving differently
 * for no reason a user could name.
 *
 * WHAT IT DOES NOT DO
 * -------------------
 * It never invents a value. A filter key that names no field on the row is
 * ignored rather than matching everything, and the search term is matched
 * against the fields the caller names — never against the whole row, which
 * would make a search for "draft" match a document whose id happens to contain
 * those letters.
 */

/** The reserved filter key the engine uses for the search box. */
const SEARCH_KEY = 'search';

/**
 * The fields a search term is matched against, as a function of the row.
 *
 * A caller names the fields that identify a record to a human. Matching the
 * whole row would make the search box a debug tool: it would find a row by its
 * internal id and by the timestamp it was created at, neither of which a user
 * typed or can see.
 */
export type SearchFields<T> = (row: T) => ReadonlyArray<string | null | undefined>;

/**
 * Narrows, sorts and pages a whole collection in memory.
 *
 * The order is fixed and matters: filter, then sort, then slice. Sorting before
 * filtering would sort rows that are about to be discarded, and slicing before
 * sorting would sort one page of a collection whose order is decided by the
 * whole of it.
 */
export function applyQuery<T>(
  rows: ReadonlyArray<T>,
  params: DataTableParams,
  searchFields: SearchFields<T>,
): { data: T[]; total: number } {
  const filtered = filterRows(rows, params.filters, searchFields);
  const sorted = sortRows(filtered, params.sort);
  const start = (params.page - 1) * params.pageSize;
  return { data: sorted.slice(start, start + params.pageSize), total: sorted.length };
}

/**
 * Applies every filter the engine reported.
 *
 * A filter value is compared against the row's own field of the same name, as a
 * string. The engine sends filter values as strings because a `<select>` and a
 * text input both produce strings, so a boolean field is compared against
 * `'true'` / `'false'` — which is exactly what a `select` filter on a boolean
 * column sends.
 */
function filterRows<T>(
  rows: ReadonlyArray<T>,
  filters: Record<string, string>,
  searchFields: SearchFields<T>,
): T[] {
  let result = [...rows];

  for (const [key, value] of Object.entries(filters)) {
    if (key === SEARCH_KEY) continue;
    result = result.filter((row) => String(readField(row, key)) === value);
  }

  const search = filters[SEARCH_KEY];
  if (search) {
    const needle = search.trim().toLowerCase();
    if (needle) {
      result = result.filter((row) =>
        searchFields(row).some((field) => (field ?? '').toLowerCase().includes(needle)),
      );
    }
  }

  return result;
}

/** Reads a named field off a row without asserting the row's shape. */
function readField(row: unknown, key: string): unknown {
  if (typeof row !== 'object' || row === null) return undefined;
  return (row as Record<string, unknown>)[key];
}

/**
 * Sorts by the first sort term the engine reported.
 *
 * One term, because the engine's header click produces one. Comparing as
 * strings keeps the order stable and predictable for the mixed value types a
 * row carries; a numeric column would need its own comparator, and none of the
 * agreements columns is numeric.
 */
function sortRows<T>(rows: T[], sort: DataTableParams['sort']): T[] {
  const term = sort[0];
  if (!term) return rows;

  return [...rows].sort((a, b) => {
    const left = String(readField(a, term.id) ?? '');
    const right = String(readField(b, term.id) ?? '');
    return term.desc ? right.localeCompare(left) : left.localeCompare(right);
  });
}

/**
 * Counts rows per saved view, so a tab can carry its own total.
 *
 * The count is of the WHOLE collection, not of the current page: a tab that
 * said "3" because three rows happened to be on screen would be a lie about the
 * collection, and the tab is a claim about the collection.
 */
export function countBy<T, K extends string>(
  rows: ReadonlyArray<T>,
  keyOf: (row: T) => K,
): Partial<Record<K, number>> {
  const counts: Partial<Record<K, number>> = {};
  for (const row of rows) {
    const key = keyOf(row);
    counts[key] = (counts[key] ?? 0) + 1;
  }
  return counts;
}
